import _ from 'lodash';
import React from 'react';

import type { ProduitsListeItemType } from '@/types/produit';

export const QUANTITE_MIN = 1;
export const QUANTITE_MAX = 99;

// Option fixe d'ajout du payeur dans le dropdown : libellé constant, indépendant de la saisie.
export const CREER_PAYEUR = '__creer_payeur__';

export type LigneComptant = {
  key: string;
  produit: ProduitsListeItemType;
  quantite: number;
};

export const clampQuantite = (value: number): number => {
  if (!_.isFinite(value)) return QUANTITE_MIN;
  return _.clamp(Math.trunc(value), QUANTITE_MIN, QUANTITE_MAX);
};

// Clamp « partiel » appliqué pendant la frappe : seul le maximum est imposé, pour ne pas
// faire réapparaître le minimum tant que l'utilisateur n'a pas quitté le champ (cf. onBlur).
export const clampQuantiteMax = (value: number): number => {
  if (!_.isFinite(value)) return value;
  return Math.min(Math.trunc(value), QUANTITE_MAX);
};

/**
 * État « brut » d'un champ de quantité : le champ peut rester vide pendant la frappe
 * (le minimum n'est imposé qu'au blur), tandis que le maximum reste appliqué en direct.
 * Partagé entre ComptantSaisie (champ unique) et ComptantTable (champ par ligne, avec
 * boutons +/- qui committent immédiatement via handleStep).
 *
 * Une quantité `null` signifie « pas encore de quantité » (aucun produit sélectionné) : le
 * champ reste alors vide, y compris au blur.
 */
export const useQuantiteSaisie = (
  quantite: number | null,
  onChange: (quantite: number) => void,
) => {
  const [raw, setRaw] = React.useState<string>(_.isNil(quantite) ? '' : String(quantite));

  React.useEffect(() => {
    setRaw(_.isNil(quantite) ? '' : String(quantite));
  }, [quantite]);

  const handleChange = React.useCallback((value: string) => {
    if (value === '') {
      setRaw('');
      return;
    }
    const parsed = Number(value);
    if (!_.isFinite(parsed)) return;
    setRaw(String(clampQuantiteMax(parsed)));
  }, []);

  const handleBlur = React.useCallback(() => {
    // Champ vide alors qu'aucune quantité n'est encore établie : on le laisse vide au lieu
    // d'imposer le minimum (le « 1 » n'apparaît qu'à la sélection d'un produit).
    if (raw === '' && _.isNil(quantite)) return;

    const finalValue = raw === '' ? QUANTITE_MIN : clampQuantite(Number(raw));
    setRaw(String(finalValue));
    onChange(finalValue);
  }, [raw, quantite, onChange]);

  const handleStep = React.useCallback(
    (delta: number) => {
      const base = raw === '' ? 0 : Number(raw);
      const finalValue = clampQuantite((_.isFinite(base) ? base : 0) + delta);
      setRaw(String(finalValue));
      onChange(finalValue);
    },
    [raw, onChange],
  );

  return { raw, handleChange, handleBlur, handleStep };
};
